import React, { useEffect, useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, Folder, FolderOpen, LockKeyhole } from 'lucide-react'

const ROLE_LABELS = {
  VIEWER: '보기',
  CONTRIBUTOR: '업로드',
  MANAGER: '관리',
}

function buildTree(items) {
  const children = new Map()
  for (const item of items || []) {
    const key = item.parentFolderId == null ? 'root' : String(item.parentFolderId)
    if (!children.has(key)) children.set(key, [])
    children.get(key).push(item)
  }
  for (const values of children.values()) {
    values.sort((left, right) => left.name.localeCompare(right.name, 'ko'))
  }
  return children
}

export default function OrgLibraryFolderTree({
  items,
  currentFolderId,
  rootLabel,
  onSelect,
  onDropItems,
  onDropFiles,
  disabled,
}) {
  const children = useMemo(() => buildTree(items), [items])
  const [expanded, setExpanded] = useState(() => new Set())
  const [dropTargetKey, setDropTargetKey] = useState(null)

  useEffect(() => {
    if (currentFolderId == null) return
    const byId = new Map((items || []).map((item) => [item.id, item]))
    setExpanded((previous) => {
      const next = new Set(previous)
      let cursor = byId.get(currentFolderId)
      while (cursor?.parentFolderId != null) {
        next.add(cursor.parentFolderId)
        cursor = byId.get(cursor.parentFolderId)
      }
      return next
    })
  }, [currentFolderId, items])

  const toggle = (folderId) => {
    setExpanded((previous) => {
      const next = new Set(previous)
      if (next.has(folderId)) next.delete(folderId)
      else next.add(folderId)
      return next
    })
  }

  const dropKey = (folderId) => folderId == null ? 'root' : String(folderId)
  const transferTypes = (event) => Array.from(event.dataTransfer?.types || [])
    .map((type) => String(type).toLowerCase())
  const isFileDrag = (event) => transferTypes(event).includes('files')
  const isItemDrag = (event) => transferTypes(event).includes('application/x-org-library-items')

  const markDropTarget = (event, folderId, writable = true) => {
    const files = isFileDrag(event)
    const libraryItems = isItemDrag(event)
    if (!files && !libraryItems) return
    event.preventDefault()
    event.stopPropagation()
    const accepted = !disabled && writable
      && ((files && onDropFiles) || (libraryItems && onDropItems))
    event.dataTransfer.dropEffect = accepted ? (files ? 'copy' : 'move') : 'none'
    setDropTargetKey(accepted ? dropKey(folderId) : null)
  }

  const clearDropTarget = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) setDropTargetKey(null)
  }

  const acceptDrop = (event, folderId, writable = true) => {
    const files = Array.from(event.dataTransfer?.files || [])
    const raw = event.dataTransfer.getData('application/x-org-library-items')
    if (!files.length && !raw) return
    event.preventDefault()
    event.stopPropagation()
    setDropTargetKey(null)
    if (disabled || !writable) return
    if (files.length && onDropFiles) {
      onDropFiles(files, folderId)
      return
    }
    if (!raw || !onDropItems) return
    try {
      const refs = JSON.parse(raw)
      if (Array.isArray(refs) && refs.length) onDropItems(refs, folderId)
    } catch {
      // Ignore drag payloads from outside the library.
    }
  }

  const renderBranch = (parentKey, depth = 0) => (children.get(parentKey) || []).map((folder) => {
    const hasChildren = Number(folder.childFolderCount || 0) > 0
      || children.has(String(folder.id))
    const open = expanded.has(folder.id)
    const selected = String(currentFolderId) === String(folder.id)
    return (
      <li key={folder.id}>
        <div
          className={`group flex h-9 items-center rounded-md ${dropTargetKey === dropKey(folder.id) ? 'bg-cyan-50 text-cyan-900 ring-2 ring-inset ring-cyan-500' : selected ? 'bg-cyan-50 text-cyan-900' : 'text-slate-600 hover:bg-slate-100'}`}
          style={{ paddingLeft: `${Math.min(depth, 6) * 14 + 4}px` }}
          onDragOver={(event) => markDropTarget(event, folder.id, folder.writable !== false)}
          onDragLeave={clearDropTarget}
          onDrop={(event) => acceptDrop(event, folder.id, folder.writable !== false)}
        >
          <button
            type="button"
            aria-label={open ? '폴더 접기' : '폴더 펼치기'}
            disabled={!hasChildren}
            onClick={() => toggle(folder.id)}
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-slate-400 disabled:opacity-0"
          >
            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
          <button
            type="button"
            onClick={() => onSelect(folder.id)}
            className="flex min-w-0 flex-1 items-center gap-2 pr-2 text-left text-sm"
          >
            {selected ? <FolderOpen className="h-4 w-4 shrink-0 text-cyan-700" /> : <Folder className="h-4 w-4 shrink-0 text-amber-600" />}
            <span className="truncate">{folder.name}</span>
            {folder.restricted && <LockKeyhole className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-label="접근 제한 폴더" />}
            <span className="ml-auto hidden shrink-0 text-[11px] text-slate-400 group-hover:inline">
              {ROLE_LABELS[folder.accessRole] || ''}
            </span>
          </button>
        </div>
        {open && hasChildren && <ul>{renderBranch(String(folder.id), depth + 1)}</ul>}
      </li>
    )
  })

  return (
    <div className="mt-4 border-t border-slate-200 pt-3">
      <p className="mb-1 px-2 text-xs font-semibold text-slate-500">폴더</p>
      <button
        type="button"
        onClick={() => onSelect(null)}
        onDragOver={(event) => markDropTarget(event, null)}
        onDragLeave={clearDropTarget}
        onDrop={(event) => acceptDrop(event, null)}
        className={`flex h-9 w-full items-center gap-2 rounded-md px-3 text-left text-sm ${dropTargetKey === 'root' ? 'bg-cyan-50 font-medium text-cyan-900 ring-2 ring-inset ring-cyan-500' : currentFolderId == null ? 'bg-cyan-50 font-medium text-cyan-900' : 'text-slate-600 hover:bg-slate-100'}`}
      >
        <FolderOpen className="h-4 w-4 shrink-0" />
        <span className="truncate">{rootLabel || '최상위'}</span>
      </button>
      <ul className="mt-1 max-h-72 overflow-y-auto">{renderBranch('root')}</ul>
    </div>
  )
}
